import styled, { keyframes } from "styled-components";

const lockPulseChoreography = keyframes`
  0%, 70% {
    transform: scale(1) translateX(0) rotate(0deg);
    color: var(--lock-base-color, #757575);
    border-color: var(--lock-base-border, rgba(0, 0, 0, 0.08));
    box-shadow: var(--lock-base-shadow, 0px 2px 8px rgba(0, 0, 0, 0.08));
  }
  72% {
    transform: scale(1.03) translateX(-1.5px) rotate(-9deg);
    color: var(--fixit);
    border-color: rgba(152, 53, 209, 0.35);
    box-shadow: 0px 4px 12px rgba(152, 53, 209, 0.18);
  }
  74% {
    transform: scale(1.05) translateX(1.5px) rotate(9deg);
    color: var(--fixit);
    border-color: rgba(152, 53, 209, 0.45);
    box-shadow: 0px 4px 14px rgba(152, 53, 209, 0.22);
  }
  76% {
    transform: scale(1.06) translateX(-1.5px) rotate(-12deg);
    color: var(--fixit);
    border-color: rgba(152, 53, 209, 0.45);
    box-shadow: 0px 4px 14px rgba(152, 53, 209, 0.22);
  }
  78% {
    transform: scale(1.06) translateX(1.5px) rotate(12deg);
    color: var(--fixit);
    border-color: rgba(152, 53, 209, 0.45);
    box-shadow: 0px 4px 14px rgba(152, 53, 209, 0.22);
  }
  80% {
    transform: scale(1.03) translateX(-1px) rotate(-6deg);
    color: var(--fixit);
    border-color: rgba(152, 53, 209, 0.35);
    box-shadow: 0px 4px 12px rgba(152, 53, 209, 0.18);
  }
  83%, 100% {
    transform: scale(1) translateX(0) rotate(0deg);
    color: var(--lock-base-color, #757575);
    border-color: var(--lock-base-border, rgba(0, 0, 0, 0.08));
    box-shadow: var(--lock-base-shadow, 0px 2px 8px rgba(0, 0, 0, 0.08));
  }
`;

export const DiscreetLockCircle = styled.div<{ $size: number; $iconSize: number }>`
  --lock-base-color: #757575;
  --lock-base-border: rgba(0, 0, 0, 0.08);
  --lock-base-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);

  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  min-width: ${(props) => props.$size}px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.2s ease;

  svg {
    font-size: ${(props) => props.$iconSize}px;
    flex-shrink: 0;
  }

  &:hover {
    --lock-base-color: var(--fixit);
    --lock-base-border: rgba(152, 53, 209, 0.4);
    --lock-base-shadow: 0px 4px 12px rgba(152, 53, 209, 0.18);
    transform: scale(1.06);
  }
`;

export const LockedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  pointer-events: auto;
  cursor: pointer;

  &:hover ${DiscreetLockCircle} {
    --lock-base-color: var(--fixit);
    --lock-base-border: rgba(152, 53, 209, 0.4);
    --lock-base-shadow: 0px 4px 12px rgba(152, 53, 209, 0.18);
    transform: scale(1.06);
  }
`;

export const DiscreetLockInner = styled.span<{ $syncDelayMs: number }>`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: var(--white);
  border: 1px solid var(--lock-base-border, rgba(0, 0, 0, 0.08));
  box-shadow: var(--lock-base-shadow, 0px 2px 8px rgba(0, 0, 0, 0.08));
  color: var(--lock-base-color, #757575);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  animation: ${lockPulseChoreography} 6s infinite ease-in-out;
  animation-delay: ${(props) => props.$syncDelayMs}ms;
`;

export const UnlockBenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: left;
`;

export const UnlockBenefitIcon = styled.span`
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 50%;
  background-color: rgba(152, 53, 209, 0.07);
  color: var(--fixit);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  svg {
    font-size: 22px;
  }
`;

export const UnlockBenefitTitle = styled.span`
  font-size: 14.5px;
  font-weight: 600;
  color: var(--dark-gray);
  line-height: 1.3;
  transition: color 0.2s ease;
`;

export const UnlockBenefitDescription = styled.span`
  font-size: 13px;
  color: var(--gray);
  line-height: 1.5;
`;

export const UnlockBenefitContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

export const UnlockBenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

